/**
 * Webhook-Safe Cart Operations
 * Ensures cart operations don't bypass webhooks and cause synchronization issues
 * 
 * CRITICAL FIX: This module addresses the issue where 'Bypass-Internal': 'true' 
 * and 'No-Queue': 'true' headers were preventing webhooks from firing properly.
 */

class WebhookSafeCartOperations {
  constructor() {
    this.isProcessing = false;
    this.operationQueue = [];
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    
    this.initializeOperations();
  }

  initializeOperations() {
    console.log('🔧 Initializing webhook-safe cart operations...');
    
    // Override existing cart operation functions with webhook-safe versions
    this.overrideCartOperations();
  }

  /**
   * Override existing cart functions with webhook-safe versions
   */
  overrideCartOperations() {
    // Store original functions for fallback
    if (!window.originalCartOperations) {
      window.originalCartOperations = {
        clearDiscounts: window.clearDiscounts,
        clearCartAtributes: window.clearCartAtributes,
        refreshCartAttributes: window.refreshCartAttributes,
        renewDummyItems: window.renewDummyItems
      };
    }

    // Replace with webhook-safe versions
    window.clearDiscounts = this.clearDiscountsSafe.bind(this);
    window.clearCartAtributes = this.clearCartAttributesSafe.bind(this);
    window.refreshCartAttributes = this.refreshCartAttributesSafe.bind(this);
    window.renewDummyItems = this.renewDummyItemsSafe.bind(this);
    
    console.log('✅ Cart operations replaced with webhook-safe versions');
  }

  /**
   * Create webhook-safe headers (removes bypass headers that prevent webhooks)
   * ENHANCED: Respects draft order lock state
   */
  createWebhookSafeHeaders(additionalHeaders = {}) {
    const baseHeaders = window.getCartHeaders ? window.getCartHeaders() : {
      'Content-Type': 'application/json'
    };

    // Add customer ID if available
    if (window.customer && window.customer.id) {
      baseHeaders['X-Customer-ID'] = window.customer.id.toString();
    }

    // Merge additional headers but EXCLUDE webhook-bypassing headers
    const safeHeaders = {
      ...baseHeaders,
      ...additionalHeaders
    };

    // CRITICAL: Remove headers that bypass webhooks
    delete safeHeaders['Bypass-Internal'];
    delete safeHeaders['No-Queue'];

    // NEW: Add draft order lock state for backend awareness
    if (window.draftOrderLockManager && window.draftOrderLockManager.isLocallyLocked()) {
      safeHeaders['X-Draft-Order-Locked'] = 'true';
      console.log('🔒 Adding draft order locked header to request');
    }

    return safeHeaders;
  }

  /**
   * Webhook-safe version of clearDiscounts
   */
  async clearDiscountsSafe() {
    try {
      console.log('🔧 Clearing discounts (webhook-safe)...');

      const request = await fetch(`${window.theme.routes.cart}.js`, {
        headers: this.createWebhookSafeHeaders()
      });
      
      const cart = await request.json();
      const token = document.querySelector('body').dataset.token;
      
      const query = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
          cart {
            id
            discountCodes {
              code
            }
          }
          userErrors {
            field
            message
          }
          warnings {
            code
            message
          }
        }
      }`;
      
      const variables = {
        "cartId": `gid://shopify/Cart/${cart.token}`,
        "discountCodes": []
      };
      
      return fetch('/api/2024-10/graphql.json', {
        method: 'POST',
        headers: this.createWebhookSafeHeaders({
          'X-Shopify-Storefront-Access-Token': token
        }),
        body: JSON.stringify({ query: query, variables: variables })
      });
      
    } catch (error) {
      console.error('❌ Webhook-safe clearDiscounts failed:', error);
      throw error;
    }
  }

  /**
   * Webhook-safe version of clearCartAtributes
   */
  async clearCartAttributesSafe(clearUser = false) {
    try {
      console.log('🔧 Clearing cart attributes (webhook-safe)...');

      // Clear localStorage items
      localStorage.removeItem('checkout_update_at');
      localStorage.removeItem('related_products');
      localStorage.removeItem('checkout_timeout');
      localStorage.removeItem('checkout_update_checkout_timeout');
      localStorage.removeItem('checkout_progress');
      localStorage.removeItem('checkout_approved');
      localStorage.removeItem('username_key');
      localStorage.removeItem('checkoutId');
      localStorage.removeItem('checkout');
      
      if (window.customer && window.customer.currentUserNumber) {
        localStorage.removeItem(`${window.customer.currentUserNumber}_bonus_response`);
        localStorage.removeItem(`${window.customer.currentUserNumber}_bonus_balance`);
      }
      
      // Prepare form data for cart attribute updates
      const formData = new FormData();
      formData.append('attributes[locked]', '');
      formData.append('attributes[expires_at]', '');
      formData.append('attributes[total_before]', '');
      formData.append('attributes[bonus_amount]', '');
      formData.append('attributes[alternatives]', '');
      formData.append('attributes[terms_1_checked]', '');
      formData.append('attributes[terms_2_checked]', '');
      formData.append('attributes[terms_3_checked]', '');
      formData.append('attributes[offline_checkout_accepted]', '');
      formData.append('attributes[offline_checkout_canceled]', '');
      
      if (clearUser) {
        // Preserve actual user note while clearing user identification
        try {
          const cartResponse = await fetch('/cart.js', {
            headers: this.createWebhookSafeHeaders()
          });
          const currentCart = await cartResponse.json();
          
          let preservedNote = '';
          if (currentCart.note) {
            const noteParts = currentCart.note.split('|');
            if (noteParts.length > 3) {
              preservedNote = noteParts.slice(3).join('|');
            } else if (!currentCart.note.includes('|') || noteParts.length < 3) {
              preservedNote = currentCart.note;
            }
          }
          
          formData.append('note', preservedNote);
        } catch (error) {
          console.warn('Could not preserve user note during cart attributes clearing:', error);
          formData.append('note', '');
        }
        
        // Clear user-related attributes
        formData.append('attributes[current_date]', '');
        formData.append('attributes[username_key]', '');
        formData.append('attributes[current_user]', '');
        formData.append('attributes[current_user_alias]', '');
      }
      
      // Use webhook-safe headers (no Bypass-Internal or No-Queue)
      const headers = this.createWebhookSafeHeaders();
      
      return fetch(`${window.theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });
      
    } catch (error) {
      console.error('❌ Webhook-safe clearCartAttributes failed:', error);
      throw error;
    }
  }

  /**
   * Webhook-safe version of refreshCartAttributes
   */
  async refreshCartAttributesSafe() {
    try {
      console.log('🔧 Refreshing cart attributes (webhook-safe)...');

      return new Promise(async (resolve, reject) => {
        try {
          const stroetmannCart = await fetch('/cart?view=json', {
            headers: this.createWebhookSafeHeaders()
          });
          
          const { current_user, current_date, current_user_alias } = await stroetmannCart.json();
          
          if (!current_user) {
            const formData = new FormData();
            formData.append('attributes[login_refresh]', 'false');
            
            try {
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: this.createWebhookSafeHeaders(),
                body: formData,
              });
            } catch (error) {
              console.warn('Cart update before logout failed, continuing:', error);
            }
            
            window.location.href = '/account/logout';
            return;
          }
          
          const formData = new FormData();
          formData.append('attributes[current_date]', current_date);
          formData.append('attributes[current_user]', current_user);
          formData.append('attributes[current_user_alias]', current_user_alias);
          formData.append('attributes[login_refresh]', 'false');
          
          try {
            const updateResponse = await fetch('/cart/update.js', {
              method: 'POST',
              headers: this.createWebhookSafeHeaders(),
              body: formData,
            });
            
            if (!updateResponse.ok) {
              console.warn(`Cart attributes refresh returned ${updateResponse.status}, but continuing...`);
              // Don't throw - cart might still be functional
            }
          } catch (updateError) {
            console.warn('Cart attributes refresh failed, but continuing:', updateError);
            // Don't throw - allow cart rebuild to continue
          }
          
          resolve();
        } catch (error) {
          console.error('❌ Webhook-safe refreshCartAttributes failed:', error);
          reject(error);
        }
      });
      
    } catch (error) {
      console.error('❌ Webhook-safe refreshCartAttributes failed:', error);
      throw error;
    }
  }

  /**
   * Webhook-safe version of renewDummyItems
   */
  async renewDummyItemsSafe(positionCount, dummyCount) {
    try {
      console.log('🔧 Renewing dummy items (webhook-safe)...');

      return new Promise(async (resolve, reject) => {
        try {
          if (!window.settings.dummy.enabled) {
            return resolve(0);
          }

          let adjustBy = 0;
          const updatedStore = JSON.parse(document.querySelector('[data-cart-items]').innerText);

          if (!window.settings.dummy.sin_enabled) {
            const dynamicLimitThreshold = 200;
            const maxDummyCount = 200;
            const requiredDummyCount = dynamicLimitThreshold - positionCount;
            const calculatedBuffer = Math.round((positionCount - 100) / 100 * maxDummyCount);
            adjustBy = positionCount < 100 && dummyCount > 0 ? dummyCount * -1 :
              positionCount >= 280 && dummyCount > 0 ? -1 :
                dummyCount < maxDummyCount && positionCount >= 100 && positionCount <= 200 ?
                  requiredDummyCount + calculatedBuffer - dummyCount :
                  0;
          } else {
            const calculatedDummyTotalCount = window.calculateDummyCount(positionCount);
            const intDummyCount = Math.round(calculatedDummyTotalCount);
            adjustBy = intDummyCount - dummyCount;
          }

          if (adjustBy > 0) {
            const items = Array
              .from({ length: adjustBy }, (x, i) => i)
              .map((id) => ({
                id: window.settings.dummy.variant_id,
                quantity: 1,
                properties: {
                  unique_identifier: window.UUID()
                }
              }));

            const add = await window.retryableFetch({
              args: [`${window.theme.routes.cartAdd}.js`, {
                method: 'POST',
                headers: this.createWebhookSafeHeaders(),
                body: JSON.stringify({ items: items })
              }]
            });
            
            const addJson = await add.json();
            document.querySelector('[data-cart-items]').innerText = JSON.stringify([...updatedStore, ...addJson.items]);
          }

          if (adjustBy < 0) {
            const inCartDummies = updatedStore.filter(({ product_id }) => window.settings.dummy.product_id == product_id);
            const removedItems = [];
            const formData = new FormData();
            
            for (let i = 0; i < inCartDummies.slice(0, adjustBy * -1).length; i++) {
              formData.append(`updates[${inCartDummies[i].key}]`, 0);
            }
            
            await window.retryableFetch({
              args: [`${window.theme.routes.cartUpdate}.js`, {
                method: 'POST',
                headers: this.createWebhookSafeHeaders(),
                body: formData
              }]
            });
            
            document.querySelector('[data-cart-items]').innerText = JSON.stringify(updatedStore.filter(({ key }) => !removedItems.includes(key)));
          }
          
          resolve(adjustBy);
        } catch (error) {
          console.error('❌ Webhook-safe renewDummyItems failed:', error);
          reject(error);
        }
      });
      
    } catch (error) {
      console.error('❌ Webhook-safe renewDummyItems failed:', error);
      throw error;
    }
  }

  /**
   * Generic webhook-safe cart operation wrapper
   */
  async safeCartOperation(operation, options = {}) {
    const { url, method = 'POST', body, additionalHeaders = {} } = options;

    const headers = this.createWebhookSafeHeaders(additionalHeaders);

    const requestOptions = {
      method,
      headers,
      ...(body && { body })
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Cart operation failed: ${response.status} ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      console.error(`❌ Webhook-safe cart operation failed (${operation}):`, error);
      throw error;
    }
  }

  /**
   * Restore original cart operations (for debugging/fallback)
   */
  restoreOriginalOperations() {
    if (window.originalCartOperations) {
      window.clearDiscounts = window.originalCartOperations.clearDiscounts;
      window.clearCartAtributes = window.originalCartOperations.clearCartAtributes;
      window.refreshCartAttributes = window.originalCartOperations.refreshCartAttributes;
      window.renewDummyItems = window.originalCartOperations.renewDummyItems;
      
      console.log('🔄 Restored original cart operations');
    }
  }
}

// Initialize webhook-safe cart operations
window.webhookSafeCartOperations = new WebhookSafeCartOperations();

// Export utility function for other modules to use
window.createWebhookSafeHeaders = (additionalHeaders = {}) => {
  return window.webhookSafeCartOperations.createWebhookSafeHeaders(additionalHeaders);
};

// Export safe cart operation wrapper
window.safeCartOperation = (operation, options) => {
  return window.webhookSafeCartOperations.safeCartOperation(operation, options);
};

console.log('✅ Webhook-Safe Cart Operations initialized');
console.log('🔧 Critical fix applied: Removed Bypass-Internal and No-Queue headers to enable webhooks');